import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import './Account.css';

const STATUS_COLORS = { pending: 'badge-warning', confirmed: 'badge-success', dispatched: 'badge-terracotta', cancelled: 'badge-error' };

export default function Account() {
  const { user, logout } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    api.get('/orders/my').then(r => { setOrders(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, [user, navigate]);

  const handleLogout = async () => { await logout(); navigate('/'); };

  if (!user) return null;

  return (
    <div className="account-page">
      <div className="account-header">
        <div className="container">
          <p className="tag">My Account</p>
          <h1 className="account-title">{user.full_name}</h1>
          <p style={{fontSize:'13px',color:'var(--gray)',marginTop:'4px'}}>{user.company_name} · {user.email}</p>
          <span className={`badge ${user.role === 'buyer' || user.role === 'admin' ? 'badge-success' : 'badge-warning'}`} style={{marginTop:'12px',display:'inline-block'}}>
            {user.role === 'buyer' ? 'Authorised Buyer' : user.role === 'admin' ? 'Administrator' : 'Standard Viewer'}
          </span>
        </div>
      </div>

      <div className="container account-body">
        <div className="account-sidebar">
          <Link to="/catalogue" className="account-nav-item">Browse Catalogue</Link>
          <Link to="/basket" className="account-nav-item">My Basket</Link>
          <button onClick={handleLogout} className="account-nav-item account-logout">Sign Out</button>
        </div>

        <div className="account-main">
          <h2 className="account-section-title">My Orders</h2>
          {loading ? (
            <div className="page-loading"><div className="spinner" /></div>
          ) : orders.length === 0 ? (
            <div className="account-empty">
              <p>You haven't placed any orders yet.</p>
              <Link to="/catalogue" className="btn-primary btn-sm">Browse Catalogue</Link>
            </div>
          ) : (
            <div className="orders-list">
              {orders.map(order => (
                <div key={order._id} className="order-card">
                  <div className="order-card-header">
                    <div>
                      <p className="order-id">Order #{order._id.slice(-8).toUpperCase()}</p>
                      <p className="order-date">{new Date(order.createdAt).toLocaleDateString('en-GB', { day:'numeric', month:'long', year:'numeric' })}</p>
                    </div>
                    <div style={{textAlign:'right'}}>
                      <span className={`badge ${STATUS_COLORS[order.status] || 'badge-gray'}`}>{order.status.charAt(0).toUpperCase() + order.status.slice(1)}</span>
                      <p className="order-total">£{Number(order.total_amount).toFixed(2)}</p>
                    </div>
                  </div>
                  <div className="order-items">
                    {order.items?.map(item => (
                      <div key={item._id} className="order-item">
                        <span className="order-item-name">{item.product_name}</span>
                        <span className="order-item-qty">× {item.quantity}</span>
                        <span className="order-item-price">£{(item.unit_price * item.quantity).toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                  {order.status === 'pending' && (
                    <div className="order-notice">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                      Awaiting confirmation. We'll email you a proforma invoice shortly.
                    </div>
                  )}
                  {order.status === 'confirmed' && (
                    <div className="order-notice order-notice-success">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>
                      Order confirmed. Please make payment via bank transfer using your proforma invoice.
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
